import { useEffect, useMemo } from 'react';

import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';
import {
  GetGroupedAssessmentsV2Response,
  useGetGroupedAssessmentsV2InfiniteQuery,
} from '~/store/services/assessments';

type UseGetAllGroupedAssessmentsV2Args = {
  patientId: string;
  sinceDate: string;
  skip?: boolean;
};

type UseGetAllGroupedAssessmentsV2Result = {
  data: Omit<GetGroupedAssessmentsV2Response, 'hasMore'> | undefined;
  isLoading: boolean;
};

// Flatten the accumulated infinite-query pages into a single surveys list.
// Surveys are keyed by externalId (a survey can span multiple pages once a
// patient has more than one page of responses for it); assessments within a
// survey are deduped by responseExternalId so a refetched page cannot leave a
// duplicate row. Uses Maps to stay linear for surveys with thousands of
// responses (e.g. ARFID Severity).
const flattenPages = (
  pages: GetGroupedAssessmentsV2Response[],
): GroupedAssessment[] => {
  const surveysByExternalId = new Map<string, GroupedAssessment>();
  const seenResponsesBySurvey = new Map<string, Set<string>>();

  for (const page of pages) {
    for (const incomingSurvey of page.surveys) {
      const existing = surveysByExternalId.get(incomingSurvey.externalId);
      if (!existing) {
        surveysByExternalId.set(incomingSurvey.externalId, {
          ...incomingSurvey,
          assessments: [...incomingSurvey.assessments],
        });
        seenResponsesBySurvey.set(
          incomingSurvey.externalId,
          new Set(incomingSurvey.assessments.map((a) => a.responseExternalId)),
        );
        continue;
      }

      const seen = seenResponsesBySurvey.get(incomingSurvey.externalId)!;
      for (const assessment of incomingSurvey.assessments) {
        if (seen.has(assessment.responseExternalId)) continue;
        seen.add(assessment.responseExternalId);
        existing.assessments.push(assessment);
      }
    }
  }

  return Array.from(surveysByExternalId.values());
};

export const useGetAllGroupedAssessmentsV2 = ({
  patientId,
  sinceDate,
  skip = false,
}: UseGetAllGroupedAssessmentsV2Args): UseGetAllGroupedAssessmentsV2Result => {
  const { data, hasNextPage, isFetching, fetchNextPage } =
    useGetGroupedAssessmentsV2InfiniteQuery(
      { patientId, sinceDate },
      { skip },
    );

  // Auto-paginate: keep pulling pages until the server reports no more. RTK
  // Query's infinite query does not auto-fetch, so we drive it here. Gating on
  // isFetching prevents racing an in-flight page (including the full re-fetch
  // that a cache invalidation triggers), so we never double-request a page.
  useEffect(() => {
    if (skip || isFetching || !hasNextPage) return;
    fetchNextPage();
  }, [skip, isFetching, hasNextPage, fetchNextPage]);

  // Only publish once the whole history has loaded, matching the previous
  // hook's "all-or-nothing" contract so downstream memoized chart/list data
  // never renders a partial series. Deliberately NOT gated on isFetching: a
  // mutation-driven re-fetch keeps isFetching true while RTK retains the prior
  // complete pages, and hasNextPage stays false (derived from that retained
  // data). Gating on isFetching here would flip the tab back to a skeleton on
  // every mutation — the regression this fix exists to avoid.
  const hasLoadedAllPages = !!data && !hasNextPage;

  // Flatten only once the whole history is loaded. Auto-pagination changes
  // `data` on every page, but the hook never exposes partial data, so
  // flattening each intermediate page would be discarded work -- and quadratic
  // over a long history (e.g. 176 pages for AssessmentEquip). Gating on
  // hasLoadedAllPages collapses that to a single flatten per settled result
  // (initial load, and again after a mutation-driven refetch).
  const surveys = useMemo(() => {
    if (!hasLoadedAllPages || !data) return undefined;
    return flattenPages(data.pages);
  }, [hasLoadedAllPages, data]);

  const result = useMemo(():
    | Omit<GetGroupedAssessmentsV2Response, 'hasMore'>
    | undefined => {
    if (!surveys) return undefined;
    return { surveys };
  }, [surveys]);

  return {
    data: result,
    isLoading: !hasLoadedAllPages && !skip,
  };
};
